/*
 * Copyright 2026 Sony Corporation
 */

import { CreateScreenshotUsingRenderTargetAsync } from '@babylonjs/core/Misc/screenshotTools';
import type { Camera } from '@babylonjs/core/Cameras/camera';

/** 16:9, and small: this is read at a couple of hundred pixels in a card. */
const WIDTH = 480;
const HEIGHT = 270;

/** Lossy on purpose. A thumbnail rides inside the publish request, so its size is the request's. */
const QUALITY = 0.7;

/**
 * The viewport as the author left it, as base64 JPEG.
 *
 * It renders to a texture rather than reading the canvas back: the engine draws
 * into an off-screen canvas and blits to each registered view, so what is in
 * the drawing buffer at any moment is whichever view happened to be last — and
 * nothing at all when the tab is hidden. A render target is the same scene
 * through the same camera at a size this decides.
 *
 * Undefined on failure. A picture is not worth losing a save over.
 */
export const captureThumbnail = async (camera: Camera): Promise<string | undefined> => {
  try {
    const dataUrl = await CreateScreenshotUsingRenderTargetAsync(
      camera.getScene().getEngine(),
      camera,
      { width: WIDTH, height: HEIGHT },
      'image/jpeg',
      // Multisampling rather than the FXAA pass the `antialiasing` flag turns
      // on: that pass is a shader, and compiling it the first time a scene is
      // saved costs seconds for an edge nobody will look at this small.
      4,
      false,
      undefined,
      false,
      false,
      true,
      QUALITY
    );
    return dataUrl.split(',')[1];
  } catch {
    return undefined;
  }
};
